import {
  computed,
  EnvironmentInjector,
  inject,
  Injectable,
  NgZone,
  runInInjectionContext,
  signal,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  arrayUnion,
  deleteDoc,
  doc,
  Firestore,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from '@angular/fire/firestore';

import type { SkWhatsNewStorage } from '@mercadoni/stark-ui';
import {
  defer,
  distinctUntilChanged,
  EMPTY,
  from,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';

import { FIRESTORE_PATHS } from '../core/config/firestore-paths';
import { AuthService } from '../core/services/auth/auth.service';
import { FirebaseAuthSyncService } from '../core/services/auth/firebase-auth-sync.service';

/** Raw Firestore shape, including the legacy single-id field kept for backward-compatible reads. */
interface OnboardingStateFirestoreData {
  seenReleaseIds?: string[];
  /** @deprecated Superseded by `seenReleaseIds`. Still read so existing users' seen state isn't lost. */
  lastSeenReleaseId?: string | null;
  completedTourIds?: string[];
  updated_at?: unknown;
}

/**
 * Seen release ids for one onboarding-state document: `seenReleaseIds` plus the legacy
 * `lastSeenReleaseId`, which only counts when it still names a catalog release.
 */
export function resolveSeenIds(
  data: OnboardingStateFirestoreData | undefined,
  catalogIds: readonly string[],
): string[] {
  const seen = new Set(data?.seenReleaseIds ?? []);
  const legacy = data?.lastSeenReleaseId;
  if (legacy && catalogIds.includes(legacy)) {
    seen.add(legacy);
  }
  return [...seen];
}

/**
 * Per-user What's New seen state in `fury-ui/config/onboarding_state/{userId}`, registered
 * as `provideSkWhatsNew({ storage: FirestoreWhatsNewStorage })` in app.config.ts.
 */
@Injectable({ providedIn: 'root' })
export class FirestoreWhatsNewStorage implements SkWhatsNewStorage {
  private readonly firestore = inject(Firestore);
  private readonly authService = inject(AuthService);
  private readonly firebaseAuthSync = inject(FirebaseAuthSyncService);
  private readonly ngZone = inject(NgZone);
  private readonly environmentInjector = inject(EnvironmentInjector);

  /**
   * Catalog release ids for the legacy-field check. Loaded lazily, like the catalog itself,
   * so the illustrations (and gsap) stay out of the initial bundle.
   */
  private readonly catalogIds$ = defer(() => import('./releases')).pipe(
    map((m) => [...m.FURY_RELEASE_IDS]),
    shareReplay(1),
  );

  /**
   * Keyed on the Auth0 user and re-keyed when the Firebase Auth custom-token exchange
   * settles — same auth-race guard as FavoritesService: a listener opened before the
   * exchange gets a terminal permission-denied, so it must re-subscribe afterwards.
   */
  private readonly session = computed(() => ({
    userId: this.authService.user()?.sub ?? null,
    firebaseUid: this.firebaseAuthSync.firebaseUser()?.uid ?? null,
  }));

  private readonly seenIds$ = toObservable(this.session).pipe(
    distinctUntilChanged((a, b) => a.userId === b.userId && a.firebaseUid === b.firebaseUid),
    tap(() => this.documentExists.set(null)),
    switchMap(({ userId }) => (userId ? this.watchDocument(userId) : EMPTY)),
    tap((data) => this.documentExists.set(data !== undefined)),
    switchMap((data) => this.catalogIds$.pipe(map((ids) => resolveSeenIds(data, ids)))),
    tap(() => this.loadError.set(null)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly loadError = signal<string | null>(null);

  /**
   * Whether the user's onboarding-state document exists: `null` until the first snapshot
   * settles (or while signed out), so a first visit is never inferred from "not loaded yet".
   */
  readonly documentExists = signal<boolean | null>(null);

  /** True only for a user whose document is confirmed missing, i.e. their first visit ever. */
  readonly isFirstVisit = computed(() => this.documentExists() === false);

  /** Dev preview guard: while true, `markSeen` completes without writing. */
  readonly writesSuspended = signal(false);

  /**
   * Full set of seen release ids on every change. Emits nothing while no user is signed
   * in, so a missing session never reads as "nothing seen".
   */
  getSeenIds(): Observable<string[]> {
    return this.seenIds$;
  }

  /** Adds `releaseId` to the user's seen set; completes without writing when signed out. */
  markSeen(releaseId: string): Observable<void> {
    return defer(() => {
      const userId = this.authService.user()?.sub;
      if (!userId || this.writesSuspended()) return of(undefined);
      // arrayUnion is applied atomically server-side, so concurrent calls (quickly advancing
      // across releases, or another tab doing the same) can't drop ids.
      return from(this.writeState(userId, { seenReleaseIds: arrayUnion(releaseId) }));
    });
  }

  /** Dev only: deletes the user's document so the next load is a first visit. */
  async clearState(): Promise<void> {
    const userId = this.authService.user()?.sub;
    if (!userId) return;
    await deleteDoc(doc(this.firestore, FIRESTORE_PATHS.ONBOARDING_STATE, userId));
  }

  /** Real-time document data (`undefined` when the document doesn't exist). */
  protected watchDocument(userId: string): Observable<OnboardingStateFirestoreData | undefined> {
    return new Observable((subscriber) =>
      onSnapshot(
        doc(this.firestore, FIRESTORE_PATHS.ONBOARDING_STATE, userId),
        (snapshot) => {
          // The Firebase SDK calls back outside Angular's zone and injection context.
          this.ngZone.run(() =>
            runInInjectionContext(this.environmentInjector, () => {
              subscriber.next(
                snapshot.exists() ? (snapshot.data() as OnboardingStateFirestoreData) : undefined,
              );
            }),
          );
        },
        (error) => {
          this.ngZone.run(() => {
            console.error('[FirestoreWhatsNewStorage] listener error:', error);
            this.loadError.set(error.message);
          });
        },
      ),
    );
  }

  /** Merges `fields` plus an `updated_at` server timestamp into the user's document. */
  protected async writeState(userId: string, fields: Record<string, unknown>): Promise<void> {
    await setDoc(
      doc(this.firestore, FIRESTORE_PATHS.ONBOARDING_STATE, userId),
      { ...fields, updated_at: serverTimestamp() },
      { merge: true },
    );
  }
}
